import { formatPlainDate, formatPlainTime } from '@airreading/common';
import { DayOfWeek, DayOfWeekLabelMap, SortedDaysOfWeek } from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

/**
 * Formats pre-grouped meeting blocks for display.
 * Takes the grouped structure from the server and formats it for display.
 * This function only handles display formatting, not grouping or sorting.
 */
export function formatGroupedMeetingBlocksForDisplay(
  groupedBlocks: {
    timeInterval: { startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime };
    days: DayOfWeek[];
  }[],
): string {
  if (!groupedBlocks.length) return '';

  // Sort grouped blocks by the earliest day of week in each group, then by start time
  const sortedBlocks = groupedBlocks.sort((a, b) => {
    // Get the earliest day for each group
    const aEarliestDay = a.days.sort(
      (day1, day2) => SortedDaysOfWeek.indexOf(day1) - SortedDaysOfWeek.indexOf(day2),
    )[0];
    const bEarliestDay = b.days.sort(
      (day1, day2) => SortedDaysOfWeek.indexOf(day1) - SortedDaysOfWeek.indexOf(day2),
    )[0];

    // Compare by earliest day
    const dayDiff = SortedDaysOfWeek.indexOf(aEarliestDay) - SortedDaysOfWeek.indexOf(bEarliestDay);
    if (dayDiff !== 0) return dayDiff;

    // If same earliest day, compare by start time
    return Temporal.PlainTime.compare(a.timeInterval.startPlainTime, b.timeInterval.startPlainTime);
  });

  const result = sortedBlocks.map(({ timeInterval, days }) => {
    const dayLabels = days.map((d) => DayOfWeekLabelMap[d].abbr).join(', ');

    const start = formatPlainTime(timeInterval.startPlainTime, { hour12: true });
    const end = formatPlainTime(timeInterval.endPlainTime, { hour12: true });

    return `${dayLabels}, ${start} - ${end}`;
  });

  return result.join('; ');
}

/**
 * Formats schedule breaks for display.
 * Takes schedule breaks data and formats it for display.
 */
export function formatScheduleBreaksForDisplay(
  scheduleBreaks: {
    startPlainDate: Temporal.PlainDate;
    endPlainDate: Temporal.PlainDate;
    name?: string | null;
  }[],
): string {
  if (!scheduleBreaks.length) return '';

  return scheduleBreaks
    .map((break_) => {
      const startDateFormatted = formatPlainDate(break_.startPlainDate);
      const endDateFormatted = formatPlainDate(break_.endPlainDate);
      const name = break_.name ? ` (${break_.name})` : '';
      if (startDateFormatted === endDateFormatted) {
        return `${startDateFormatted}${name}`;
      }
      return `${startDateFormatted} - ${endDateFormatted}${name}`;
    })
    .join(', ');
}
